import {
  Badge,
  Button,
  Card,
  CardContent,
  ErrorState,
  LoadingState,
  PageHeader,
  StatPill,
  StaticMap,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  ArrowLeft,
  Building2,
  ClipboardCheck,
  FlaskConical,
  FolderOpen,
  Hammer,
  ListTodo,
  Mail,
  Pencil,
  Phone,
  RefreshCcw,
  ShieldAlert,
  Trash2,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { fetchMe } from '../../../api/auth.js';
import {
  deleteBuilding,
  fetchActivity,
  fetchBuilding,
  listContacts,
  listUnits,
  regeocodeBuilding,
} from '../../../api/buildings.js';
import { listWorkOrders } from '../../../api/work-orders.js';
import { ApiError } from '../../../lib/api-client.js';
import { CreateWorkOrderSheet } from '../work-orders/create-sheet.js';

import { TasksListPage } from '../reminders/list.js';

import { BuildingComplianceTab } from './building-compliance.js';
import { BuildingContactsCard } from './building-contacts.js';
import { BuildingLeadTab } from './building-lead-tab.js';
import { BuildingRecordsTab } from './building-records-tab.js';
import { BuildingUnitsTable } from './building-units.js';
import { BuildingViolationsTab } from './building-violations-tab.js';

export function BuildingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const buildingQuery = useQuery({
    queryKey: ['building', id],
    queryFn: () => fetchBuilding(id!),
    enabled: Boolean(id),
  });
  const unitsQuery = useQuery({
    queryKey: ['building', id, 'units'],
    queryFn: () => listUnits(id!),
    enabled: Boolean(id),
  });
  const contactsQuery = useQuery({
    queryKey: ['building', id, 'contacts'],
    queryFn: () => listContacts(id!),
    enabled: Boolean(id),
  });
  const activityQuery = useQuery({
    queryKey: ['building', id, 'activity'],
    queryFn: () => fetchActivity(id!),
    enabled: Boolean(id),
  });
  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const canBulkAct =
    meQuery.data?.user.role === 'admin' ||
    meQuery.data?.user.role === 'building_manager' ||
    meQuery.data?.user.isSuperAdmin === true;

  const regeocodeMutation = useMutation({
    mutationFn: () => regeocodeBuilding(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['building', id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteBuilding(id!),
    onSuccess: () => navigate('/buildings'),
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'CONFLICT') {
        const details = err.details as { reason?: string; blockers?: Array<{ kind: string; count: number; label?: string }> } | null;
        if (details?.reason === 'BLOCKED_BY_DEPENDENTS' && details.blockers) {
          const list = details.blockers
            .map((b) => `${b.label ?? b.kind}: ${b.count}`)
            .join(', ');
          setDeleteError(`Archive blocked by ${list}. Remove them first, then try again.`);
          return;
        }
      }
      setDeleteError(err instanceof Error ? err.message : 'Could not archive this building');
    },
  });

  if (buildingQuery.isLoading) return <LoadingState rows={6} />;
  if (buildingQuery.isError || !buildingQuery.data) {
    return <ErrorState description="Could not load this building." />;
  }

  const detail = buildingQuery.data;
  const b = detail.building;

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: 'Buildings', href: '/buildings' }, { label: b.name }]}
        title={b.name}
        description={
          <>
            {b.addressLine1}
            {b.addressLine2 ? `, ${b.addressLine2}` : ''} · {b.city}, {b.state} {b.postalCode}
          </>
        }
        meta={
          <>
            {b.bin ? <StatPill value={`BIN ${b.bin}`} tone="info" /> : <StatPill value="No BIN" tone="neutral" />}
            {b.bbl ? <StatPill value={`BBL ${b.bbl}`} tone="info" /> : null}
            {b.borough ? (
              <Badge variant="outline" className="capitalize">
                {b.borough.replace('_', ' ')}
              </Badge>
            ) : null}
            <StatPill
              value={`${detail.unitCount} ${detail.unitCount === 1 ? 'unit' : 'units'}`}
              tone="neutral"
            />
            {detail.stats.openViolations != null ? (
              <StatPill
                value={`${detail.stats.openViolations} open ${
                  detail.stats.openViolations === 1 ? 'violation' : 'violations'
                }`}
                tone={detail.stats.openViolations > 0 ? 'danger' : 'success'}
                withDot
              />
            ) : null}
            {b.geocodeSource === 'manual' ? (
              <Badge variant="outline">Manual override</Badge>
            ) : null}
          </>
        }
        actions={
          <>
            <Link to="/buildings">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={() => regeocodeMutation.mutate()}
              disabled={regeocodeMutation.isPending || b.geocodeSource === 'manual'}
              title={
                b.geocodeSource === 'manual'
                  ? 'Clear BIN/BBL before re-geocoding'
                  : 'Re-run NYC GeoSearch for this address'
              }
            >
              <RefreshCcw className="h-4 w-4" />
              Re-geocode
            </Button>
            <Link to={`/buildings/${b.id}/edit`}>
              <Button variant="outline" size="sm">
                <Pencil className="h-4 w-4" /> Edit
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (confirm('Archive this building? Active units must be archived first.')) {
                  setDeleteError(null);
                  deleteMutation.mutate();
                }
              }}
              disabled={deleteMutation.isPending}
              className="text-danger hover:text-danger"
            >
              <Trash2 className="h-4 w-4" /> Archive
            </Button>
          </>
        }
      />

      {deleteError ? (
        <div className="mb-6">
          <ErrorState description={deleteError} />
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="units">
            <TabsList>
              <TabsTrigger value="units">
                <Building2 className="h-3.5 w-3.5" /> Units
              </TabsTrigger>
              <TabsTrigger value="contacts">
                <Users className="h-3.5 w-3.5" /> Contacts
              </TabsTrigger>
              <TabsTrigger value="work-orders">
                <Hammer className="h-3.5 w-3.5" /> Work orders
              </TabsTrigger>
              <TabsTrigger value="tasks">
                <ListTodo className="h-3.5 w-3.5" /> Tasks
              </TabsTrigger>
              <TabsTrigger value="compliance">
                <ClipboardCheck className="h-3.5 w-3.5" /> Compliance
              </TabsTrigger>
              <TabsTrigger value="records">
                <FolderOpen className="h-3.5 w-3.5" /> Records
              </TabsTrigger>
              <TabsTrigger value="lead">
                <FlaskConical className="h-3.5 w-3.5" /> Lead
              </TabsTrigger>
              <TabsTrigger value="violations">
                <ShieldAlert className="h-3.5 w-3.5" /> Violations
              </TabsTrigger>
              <TabsTrigger value="activity">
                <Activity className="h-3.5 w-3.5" /> Activity
              </TabsTrigger>
            </TabsList>
            <TabsContent value="units" className="mt-4">
              <BuildingUnitsTable
                buildingId={b.id}
                units={unitsQuery.data?.units ?? []}
                isLoading={unitsQuery.isLoading}
              />
            </TabsContent>
            <TabsContent value="contacts" className="mt-4">
              <BuildingContactsCard
                buildingId={b.id}
                contacts={contactsQuery.data?.contacts ?? []}
                isLoading={contactsQuery.isLoading}
              />
            </TabsContent>
            <TabsContent value="work-orders" className="mt-4">
              <BuildingWorkOrdersTab buildingId={b.id} />
            </TabsContent>
            <TabsContent value="tasks" className="mt-4">
              <TasksListPage buildingId={b.id} />
            </TabsContent>
            <TabsContent value="compliance" className="mt-4">
              <BuildingComplianceTab buildingId={b.id} />
            </TabsContent>
            <TabsContent value="records" className="mt-4">
              <BuildingRecordsTab buildingId={b.id} />
            </TabsContent>
            <TabsContent value="lead" className="mt-4">
              <BuildingLeadTab buildingId={b.id} canBulkAct={canBulkAct} />
            </TabsContent>
            <TabsContent value="violations" className="mt-4">
              <BuildingViolationsTab buildingId={b.id} />
            </TabsContent>
            <TabsContent value="activity" className="mt-4">
              <ActivityFeed entries={activityQuery.data?.entries ?? []} isLoading={activityQuery.isLoading} />
            </TabsContent>
          </Tabs>
        </div>

        <aside className="space-y-4">
          <StaticMap latitude={b.latitude} longitude={b.longitude} />
          <Card>
            <CardContent className="space-y-3 p-5 text-sm">
              <DefinitionRow label="Borough" value={b.borough?.replace('_', ' ') ?? '—'} capitalize />
              <DefinitionRow label="Geocode source" value={b.geocodeSource ?? '—'} />
              {b.geocodeConfidence ? (
                <DefinitionRow label="Confidence" value={b.geocodeConfidence} />
              ) : null}
              <DefinitionRow
                label="Coordinates"
                value={
                  b.latitude != null && b.longitude != null
                    ? `${b.latitude.toFixed(5)}, ${b.longitude.toFixed(5)}`
                    : '—'
                }
              />
              <DefinitionRow
                label="Geocoded at"
                value={b.geocodedAt ? new Date(b.geocodedAt).toLocaleString() : 'Never'}
              />
              {b.notes ? <DefinitionRow label="Notes" value={b.notes} /> : null}
            </CardContent>
          </Card>
        </aside>
      </div>
    </>
  );
}

function DefinitionRow({
  label,
  value,
  capitalize,
}: {
  label: string;
  value: React.ReactNode;
  capitalize?: boolean;
}) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-2">
      <span className="text-xs uppercase tracking-wide text-fg-muted">{label}</span>
      <span className={capitalize ? 'capitalize text-fg-secondary' : 'text-fg-secondary'}>
        {value}
      </span>
    </div>
  );
}

function ActivityFeed({
  entries,
  isLoading,
}: {
  entries: Array<{
    id: string;
    action: string;
    createdAt: string;
    actorKind: string;
    metadata: unknown;
  }>;
  isLoading: boolean;
}) {
  if (isLoading) return <LoadingState rows={4} />;
  if (entries.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-fg-secondary">
          No activity recorded yet for this building.
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="divide-y divide-border p-0 text-sm">
        {entries.map((entry) => (
          <div key={entry.id} className="flex items-start justify-between gap-3 px-5 py-3">
            <div>
              <div className="font-medium text-fg">{prettyAction(entry.action)}</div>
              <div className="text-xs text-fg-muted">
                {entry.actorKind === 'super_admin' ? 'super-admin' : 'user'} ·{' '}
                {new Date(entry.createdAt).toLocaleString()}
              </div>
            </div>
            {renderMetadata(entry.metadata)}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function prettyAction(action: string): string {
  switch (action) {
    case 'building.created':
      return 'Building created';
    case 'building.updated':
      return 'Building updated';
    case 'building.archived':
      return 'Building archived';
    case 'building.contact_added':
      return 'Contact added';
    case 'building.contact_updated':
      return 'Contact updated';
    case 'building.contact_removed':
      return 'Contact removed';
    case 'unit.created':
      return 'Unit added';
    case 'unit.bulk_created':
      return 'Units added in bulk';
    case 'unit.updated':
      return 'Unit updated';
    case 'unit.archived':
      return 'Unit archived';
    default:
      return action;
  }
}

function renderMetadata(metadata: unknown): React.ReactNode {
  if (!metadata || typeof metadata !== 'object') return null;
  const meta = metadata as Record<string, unknown>;
  if (typeof meta.count === 'number') {
    return <span className="text-xs text-fg-muted">{meta.count} added</span>;
  }
  if (typeof meta.unitNumber === 'string') {
    return <span className="text-xs text-fg-muted">Unit {meta.unitNumber}</span>;
  }
  if (typeof meta.name === 'string' && typeof meta.role === 'string') {
    return (
      <span className="text-xs text-fg-muted">
        {meta.name} ({meta.role})
      </span>
    );
  }
  return null;
}

function BuildingWorkOrdersTab({ buildingId }: { buildingId: string }) {
  const navigate = useNavigate();
  const [openCreate, setOpenCreate] = useState(false);
  const woQuery = useQuery({
    queryKey: ['work-orders', { buildingId }],
    queryFn: () => listWorkOrders({ buildingId, limit: 25 }),
  });

  const rows = woQuery.data?.workOrders ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Link
          to={`/work-orders?buildingId=${buildingId}`}
          className="text-sm text-primary hover:underline"
        >
          View all work orders for this building →
        </Link>
        <Button size="sm" onClick={() => setOpenCreate(true)}>
          <Hammer className="h-3.5 w-3.5" />
          New work order
        </Button>
      </div>
      {woQuery.isLoading ? (
        <LoadingState rows={3} />
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm text-fg-secondary">
            No work orders for this building yet.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="divide-y divide-border p-0">
            {rows.map((wo) => (
              <button
                key={wo.id}
                onClick={() => navigate(`/work-orders/${wo.id}`)}
                className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left text-sm hover:bg-surface-muted"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span className="font-mono text-xs text-fg-muted">{wo.displayNumber}</span>
                  <span className="truncate font-medium">{wo.title}</span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-xs text-fg-muted">
                    {wo.severity}
                  </span>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>
      )}
      <CreateWorkOrderSheet
        open={openCreate}
        onOpenChange={setOpenCreate}
        buildingId={buildingId}
        onCreated={(wo) => {
          setOpenCreate(false);
          navigate(`/work-orders/${wo.id}`);
        }}
      />
    </div>
  );
}

// Re-export icons used elsewhere if any
export { Mail, Phone };
